import React, { useMemo, useState, useEffect } from 'react';
import { loadTaxonomy } from '@/lib/taxonomy-loader';
import { CloseIcon } from '../../components/shared/Icons';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Drill } from './types/drill-types';

import { Save, X } from 'lucide-react';

interface TagChange {
    category: string;
    value: string;
}
import logger from '@/lib/logging';

interface BulkEditChanges {
    add?: TagChange;
    remove: TagChange[];
}

interface BulkEditTagsPopinProps {
    isExpanded: boolean;
    onClose: () => void;
    onApply: (changes: BulkEditChanges) => Promise<void> | void;
    selectedDrills: Drill[];
}

interface TagSummary {
    category: string;
    value: string;
    count: number;
}

const BulkEditTagsPopin: React.FC<BulkEditTagsPopinProps> = ({ isExpanded, onClose, onApply, selectedDrills }) => {
    const [addCategory, setAddCategory] = useState<string>('');
    const [addValue, setAddValue] = useState<string>('');
    const [tagsToRemove, setTagsToRemove] = useState<Set<string>>(new Set());
    const [tagSearchTerm, setTagSearchTerm] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [taxonomy, setTaxonomy] = useState<{ [key: string]: { type: string; options: string[] } }>({});

    // Load full taxonomy from RuleDoc-Drill-Taxonomy.json
    useEffect(() => {
        const initializeTaxonomy = async () => {
            const data = await loadTaxonomy();
            if (data.categories) {
                setTaxonomy(data.categories);
            }
        };
        initializeTaxonomy();
    }, []);

    // Get all existing tags across selected drills
    const existingTags = useMemo<TagSummary[]>(() => {
        const tagMap = new Map<string, TagSummary>();

        selectedDrills.forEach(drill => {
            if (drill.tags) {
                Object.entries(drill.tags).forEach(([category, value]) => {
                    if (value) {
                        const values = Array.isArray(value) ? value : [value];
                        values.forEach(v => {
                            if (v && typeof v === 'string' && v.trim()) {
                                const key = `${category}::${v}`;
                                const existing = tagMap.get(key);
                                if (existing) {
                                    existing.count += 1;
                                } else {
                                    tagMap.set(key, { category, value: v, count: 1 });
                                }
                            }
                        });
                    }
                });
            }
        });

        return Array.from(tagMap.values()).sort((a, b) => {
            // Sort by category first, then by value
            if (a.category !== b.category) {
                return a.category.localeCompare(b.category);
            }
            return a.value.localeCompare(b.value);
        });
    }, [selectedDrills]);

    // Filter tags based on search term
    const filteredTags = useMemo(() => {
        if (!tagSearchTerm.trim()) {
            return existingTags;
        }

        const searchLower = tagSearchTerm.toLowerCase();
        return existingTags.filter(tag =>
            tag.category.toLowerCase().includes(searchLower) ||
            tag.value.toLowerCase().includes(searchLower)
        );
    }, [existingTags, tagSearchTerm]);

    const handleTagToggle = (category: string, value: string) => {
        const key = `${category}::${value}`;
        const newTagsToRemove = new Set(tagsToRemove);

        if (newTagsToRemove.has(key)) {
            newTagsToRemove.delete(key);
        } else {
            newTagsToRemove.add(key);
        }

        setTagsToRemove(newTagsToRemove);
    };

    const categoryOptions = useMemo(() =>
        Object.keys(taxonomy)
            .filter(key => taxonomy[key].type !== 'text')
            .sort(),
        [taxonomy]);

    const hasChanges = (addCategory.trim() && addValue.trim()) || tagsToRemove.size > 0;

    const handleApply = async () => {
        if (!hasChanges || isSubmitting) return;

        const changes: BulkEditChanges = {
            remove: Array.from(tagsToRemove).map(tagKey => {
                const [category, value] = tagKey.split('::');
                return { category, value };
            })
        };

        if (addCategory.trim() && addValue.trim()) {
            changes.add = { category: addCategory.trim(), value: addValue.trim() };
        }

        try {
            setIsSubmitting(true);
            await onApply(changes);
            setAddCategory('');
            setAddValue('');
            setTagsToRemove(new Set());
            setTagSearchTerm('');
            onClose();
        } catch (error) {
            logger.error('Error applying tag changes:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isExpanded) {
        return null;
    }

    return (
        <Card className="mb-6 border shadow-lg">
            <CardHeader className="flex flex-row items-start justify-between space-y-0">
                <div>
                    <CardTitle>Edit Tags</CardTitle>
                    <CardDescription>
                        Manage tags for {selectedDrills.length} selected drill{selectedDrills.length !== 1 ? 's' : ''}.
                    </CardDescription>
                </div>
                <Button variant="ghost" size="icon" onClick={onClose}>
                    <CloseIcon />
                </Button>
            </CardHeader>
            <CardContent className="space-y-6">
                <div className="grid gap-6 lg:grid-cols-2">
                    <div className="space-y-3">
                        <Label>Category</Label>
                        <Select value={addCategory} onValueChange={setAddCategory}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                            <SelectContent>
                                {categoryOptions.map(option => (
                                    <SelectItem key={option} value={option}>
                                        {option}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Label htmlFor="add-tag-value" className="pt-2">Tag value</Label>
                        <Input
                            id="add-tag-value"
                            value={addValue}
                            onChange={(e) => setAddValue(e.target.value)}
                            placeholder="Enter new tag value"
                        />
                    </div>

                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <Label>Remove existing tags</Label>
                            {tagsToRemove.size > 0 && (
                                <Badge variant="secondary">{tagsToRemove.size} selected</Badge>
                            )}
                        </div>
                        {existingTags.length > 0 && (
                            <Input
                                value={tagSearchTerm}
                                onChange={(e) => setTagSearchTerm(e.target.value)}
                                placeholder="Search tags..."
                            />
                        )}
                        {existingTags.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No tags found in selected drills.</p>
                        ) : filteredTags.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                                {tagSearchTerm ? `No tags matching "${tagSearchTerm}".` : 'No tags found in selected drills.'}
                            </p>
                        ) : (
                            <ScrollArea className="h-56 rounded border">
                                <div className="p-2 space-y-2">
                                    {filteredTags.map(tag => {
                                        const key = `${tag.category}::${tag.value}`;
                                        const isSelected = tagsToRemove.has(key);
                                        return (
                                            <button
                                                key={key}
                                                type="button"
                                                onClick={() => handleTagToggle(tag.category, tag.value)}
                                                className={`w-full rounded-md border px-3 py-2 text-left transition ${isSelected
                                                    ? 'border-destructive/50 bg-destructive/10 text-destructive'
                                                    : 'border-border hover:bg-muted'
                                                    }`}
                                            >
                                                <div className="flex items-center justify-between text-sm font-medium">
                                                    <span>{tag.category}</span>
                                                    <Badge variant="outline">{tag.count}/{selectedDrills.length}</Badge>
                                                </div>
                                                <div className="text-xs text-muted-foreground">{tag.value}</div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </ScrollArea>
                        )}
                    </div>
                </div>

                <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={onClose} disabled={isSubmitting} className="h-10">
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                    </Button>
                    <Button onClick={handleApply} disabled={!hasChanges || isSubmitting} className="h-10">
                        <Save className="w-4 h-4 mr-2" />
                        {isSubmitting ? 'Saving...' : 'Apply Changes'}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

export default BulkEditTagsPopin;
